/*
 * Il bottone "Add to wishlist" del tema, collegato all app Swish (Wishlist King).
 * L app disegna i suoi bottoni, ma qui il posto lo decide il Figma: teniamo il nostro e gli
 * attacchiamo la sua API. Se l app non e' caricata il bottone non compare, cosi' non resta a video
 * un comando che non fa niente — che e' il difetto da cui siamo partiti.
 */
class WishlistToggle extends HTMLElement {
	connectedCallback() {
		// l elemento puo' essere riattaccato al DOM: senza questa pulizia si accumulerebbero
		// intervalli e iscrizioni, uno per ogni giro
		this.disconnectedCallback();
		this.bottone = this.querySelector('button');
		this.handle = this.dataset.handle;
		if (!this.bottone || !this.handle) return;

		// l etichetta e' rich text e puo' contenere corsivi o grassetti: si tiene l HTML, se no dopo
		// un giro di andata e ritorno la formattazione scelta dal cliente sparirebbe
		this.etichette = {
			fuori: this.bottone.innerHTML,
			dentro: this.dataset.labelAdded || this.bottone.innerHTML,
		};
		this.dentro = false;
		this.bottone.addEventListener('click', () => this.#alterna());
		this.#aspettaApp();
	}

	disconnectedCallback() {
		clearInterval(this.attesa);
		this.iscrizione?.unsubscribe?.();
	}

	// Non basta che l oggetto esista: deve esserci anche il metodo. Se ci si fida del solo namespace,
	// il primo click di chi arriva nell istante sbagliato finisce nel catch e a video non succede niente.
	#appPronta() {
		return typeof window.WishlistKing?.addWishlistItem === 'function';
	}

	#aspettaApp() {
		if (this.#appPronta()) {
			this.#accendi();
			return;
		}
		this.hidden = true;
		// mezzo minuto: su una rete lenta lo script dell app arriva parecchio dopo la pagina
		let tentativi = 60;
		this.attesa = setInterval(() => {
			if (this.#appPronta()) {
				this.#accendi();
				clearInterval(this.attesa);
			} else if (--tentativi <= 0) {
				clearInterval(this.attesa);
			}
		}, 500);
	}

	#accendi() {
		this.hidden = false;
		this.#osserva();
	}

	/*
	 * Lo stato va CHIESTO all app, non indovinato: se no un prodotto gia' salvato si ripresenta come
	 * "da salvare", e per toglierlo servono due click — il primo dei quali fa una cosa che nessuno ha
	 * chiesto. L osservabile risolve anche il caso opposto: se l articolo viene tolto dalla pagina
	 * wishlist, qui l etichetta si aggiorna da sola.
	 * ponytail: `_state` e' API interna dell app. Se un aggiornamento la cambia, la guardia qui sotto
	 * lascia il bottone come semplice interruttore invece di romperlo.
	 */
	#osserva() {
		const stato = window.WishlistKing?._state;
		if (typeof stato?.observeWishlist !== 'function') return;
		try {
			this.iscrizione = stato.observeWishlist({ wishlistId: 'mine' }).subscribe((lista) => {
				const voci = lista?.items ?? [];
				const variante = this.#variante();
				// si tiene da parte la voce, non solo il si/no: per toglierla servira' il suo id
				this.voce = voci.find(
					(voce) =>
						voce.product?.handle === this.handle &&
						(!variante || voce.variantId === variante)
				);
				this.#mostra(Boolean(this.voce));
			});
		} catch (errore) {
			console.warn('[wishlist] stato non leggibile, resta un interruttore semplice', errore);
		}
	}

	#mostra(dentro) {
		this.dentro = dentro;
		this.bottone.innerHTML = dentro ? this.etichette.dentro : this.etichette.fuori;
		this.classList.toggle('is-in-lista', dentro);
	}

	// la variante conta: due cinturini dello stesso prodotto sono due voci diverse in lista
	#variante() {
		const campo =
			this.closest('form')?.querySelector('[name="id"]') ||
			document.querySelector('.pdp__layout [name="id"]');
		const valore = Number(campo?.value);
		return Number.isFinite(valore) && valore > 0 ? valore : undefined;
	}

	async #alterna() {
		const app = window.WishlistKing;
		if (!this.#appPronta() || this.bottone.disabled) return;
		this.bottone.disabled = true;
		try {
			/*
			 * Le due chiamate NON sono simmetriche, e la differenza non si vede finche' non si
			 * controlla la lista: si aggiunge con prodotto e variante, ma si toglie con l ID DELLA
			 * VOCE (`wishlistItemId`). Passando prodotto e variante alla rimozione l app risponde
			 * 400 e l articolo resta in lista, mentre l etichetta cambia lo stesso: sembra fatto e
			 * non e' fatto. L id arriva dall osservabile qui sopra.
			 */
			if (this.dentro) {
				if (!this.voce?.id) return;
				await app.removeWishlistItem({ wishlistItemId: this.voce.id });
			} else {
				await app.addWishlistItem({ productHandle: this.handle, variantId: this.#variante() });
			}
			/*
			 * Con l osservatore attivo e' lui a riscrivere l etichetta, e il bottone resta un
			 * interruttore. Senza, l id della voce non si conosce e **togliere da qui non e'
			 * possibile**: allora il bottone dichiara che il prodotto e' salvato e si ferma, invece
			 * di restare cliccabile e non fare niente al secondo click. Si toglie dalla pagina
			 * wishlist. (Prima qui c era un `#mostra(!this.dentro)` che prometteva un interruttore
			 * che in quel ramo non poteva funzionare.)
			 */
			if (!this.iscrizione) {
				this.#mostra(true);
				this.bloccato = true;
			}
		} catch (errore) {
			// l app apre da sola la finestra di accesso quando serve il login. Se invece la chiamata
			// fallisce davvero, l etichetta resta com era: il click non finge di essere riuscito.
			console.warn('[wishlist]', errore);
		} finally {
			this.bottone.disabled = Boolean(this.bloccato);
		}
	}
}

if (!customElements.get('wishlist-toggle')) customElements.define('wishlist-toggle', WishlistToggle);

const verificati = new WeakSet();
const inVerifica = new WeakSet();

document.addEventListener(
	'submit',
	async (event) => {
		const form = event.target;
		const card = form.closest('wishlist-product-card');
		if (!card || !form.matches('.wk-form') || verificati.delete(form)) return;
		event.preventDefault();
		event.stopImmediatePropagation();
		if (inVerifica.has(form)) return;
		const link = card.querySelector('a[href]');
		if (!link) return;
		const url = new URL(link.href);
		const variante = new FormData(form).get('id');
		if (variante) url.searchParams.set('variant', variante);
		inVerifica.add(form);
		try {
			// Anche le vecchie wishlist devono rispettare la stessa regola della scheda prodotto.
			// ponytail: una lettura della PDP per acquisto; view minima se la latenza diventa un problema.
			const response = await fetch(url, { cache: 'no-store' });
			if (!response.ok) throw new Error('Product unavailable');
			const pagina = new DOMParser().parseFromString(await response.text(), 'text/html');
			if (new FormData(form).get('id') !== variante) return;
			if (!pagina.querySelector('.pdp-buy__form')) {
				window.location.assign(url.href);
				return;
			}
			verificati.add(form);
			form.requestSubmit(event.submitter);
		} catch {
			window.location.assign(url.href);
		} finally {
			inVerifica.delete(form);
		}
	},
	true,
);
